import { useFormState } from 'react-hook-form';
import { TextInput } from '@kcftech/design-system';
import { AssetCategoryParameterType } from '@kcftech/prometheus-client';
import {
    getMaxValueRules,
    getMinValueRules,
    getNumericDefaultValueRules,
    MinMaxInputContainer,
} from 'pages/Settings/AssetType/parameter_form/shared';
import { AssetParameterFormInputProps } from 'pages/Settings/AssetType/parameter_form/types';
import { useEffect, useMemo } from 'react';

export function AssetParameterIntegerFormContent({
    form,
    parameter,
}: AssetParameterFormInputProps) {
    const {
        register,
        watch,
        setValue,
        control,
    } = form;

    const {
        errors,
    } = useFormState({
        control,
    });

    const formData = watch();
    const { type, minValue, maxValue } = formData;

    if (type !== AssetCategoryParameterType.Integer) {
        throw new Error('Not a valid type');
    }

    const minValueRules = useMemo(
        () => getMinValueRules(type, maxValue),
        [maxValue, type],
    );

    const maxValueRules = useMemo(
        () => getMaxValueRules(type, minValue),
        [minValue, type],
    );

    const defaultValueRules = useMemo(
        () =>
            getNumericDefaultValueRules({
                type,
                minValue,
                maxValue,
                minValueInclusive: null,
                maxValueInclusive: null,
            }),
        [maxValue, minValue, type],
    );

    useEffect(() => {
        if (parameter?.CategoryParameterType === AssetCategoryParameterType.Integer) {
            setValue(
                'minValue',
                parameter.MinValue !== undefined ? `${parameter.MinValue}` : null,
            );
            setValue(
                'maxValue',
                parameter.MaxValue !== undefined ? `${parameter.MaxValue}` : null,
            );
            setValue('defaultValue', parameter.DefaultValue ?? '');
        } else {
            setValue('minValue', null);
            setValue('maxValue', null);
            setValue('defaultValue', '');
        }

        return () => {
            setValue('minValue', null);
            setValue('maxValue', null);
            setValue('defaultValue', '');
        };
    }, [
        type,
        setValue,
        parameter?.CategoryParameterType,
        parameter?.DefaultValue,
        parameter?.MinValue,
        parameter?.MaxValue,
    ]);

    return (
        <>
            <MinMaxInputContainer>
                <TextInput
                    ariaLabel="min value"
                    label="Min Value"
                    placeholder="Enter a minimum value"
                    error={errors.minValue?.message}
                    width="26rem"
                    {...register('minValue', minValueRules)}
                />

                <TextInput
                    ariaLabel="max value"
                    label="Max Value"
                    placeholder="Enter a maximum value"
                    error={errors.maxValue?.message}
                    width="26rem"
                    {...register('maxValue', maxValueRules)}
                />
            </MinMaxInputContainer>

            <TextInput
                ariaLabel="default value"
                label="Default Value"
                placeholder="Enter a default value"
                error={errors.defaultValue?.message}
                {...register('defaultValue', defaultValueRules)}
            />
        </>
    );
}
